// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import {
  type StrongOmit,
  type Label,
  type LabelTypeId,
  type CreateOrUpdateLabelRequest,
  isHexTriplet,
} from '@evolvemep/api-ts';
import { Button, type ButtonProps, useMantineTheme } from '@mantine/core';
import { useForm } from '@mantine/form';

import { convertMantineColorToHex } from 'components/Mantine/ColorPickers/colorHelpers';
import { ColorAndNameFields } from 'components/Mantine/Labels/ManageLabelsModal/ColorAndNameFields';
import { FormPopover, useFormPopover } from 'components/Mantine/Popovers/FormPopover';
import { useLabels } from 'hooks/typeCacheHooks/useLabels/useLabels';

type Props = {
  /** The ID of the label type under which the new label will be created. */
  labelTypeId: LabelTypeId;
  onLabelUpdated?: (label: Label) => void;
  buttonProps?: ButtonProps;
};

const CreateLabelFields = ({ labelTypeId, onLabelUpdated }: Pick<Props, 'labelTypeId' | 'onLabelUpdated'>) => {
  const { createLabel } = useLabels(labelTypeId);

  const theme = useMantineTheme();
  const defaultColorHex = convertMantineColorToHex('gray.1', theme.colors);
  const form = useForm<StrongOmit<CreateOrUpdateLabelRequest, 'labelTypeId'>>({
    initialValues: {
      name: '',
      color: defaultColorHex,
    },
    validate: {
      name: (v) => !v,
      color: (v) => !isHexTriplet(v),
    },
  });

  const onSubmit = async (values: typeof form.values) => {
    await createLabel(values).then(onLabelUpdated);
  };

  useFormPopover(form, onSubmit);

  return <ColorAndNameFields form={form} maxNameLength={64} />;
};

export const CreateLabelPopover = ({ labelTypeId, onLabelUpdated, buttonProps = {} }: Props) => {
  const [opened, setOpened] = useState(false);

  return (
    <FormPopover
      title="New label"
      opened={opened}
      onClose={() => setOpened(false)}
      target={
        <Button onClick={() => setOpened(true)} {...buttonProps}>
          New label
        </Button>
      }
    >
      <CreateLabelFields labelTypeId={labelTypeId} onLabelUpdated={onLabelUpdated} />
    </FormPopover>
  );
};
